import type { Reference, ServiceRequest } from '@medplum/fhirtypes'
import * as codeSystems from '@seen/core/codeSystems'
import { OrderCategory } from '@seen/core/models/orders/constants'
import { Order } from '@seen/core/models/orders/Order'
import { ExtendedRequestGroup } from '@seen/core/models/requestGoup'
import { resolveReference } from '@seen/core/references'
import { ContextMenuItem } from '@seen/ui'
import { IconExternalLink } from '@tabler/icons-react'
import { addHours, subHours } from 'date-fns'
import { type ReactNode, useMemo } from 'react'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface MenuQuestLinkItemProps {
  to: ServiceRequest | Reference<ServiceRequest> | Order
}

export function ContextMenuQuestLinkItem({
  to,
}: MenuQuestLinkItemProps): ReactNode {
  const labReferenceId = useLabReferenceId(to)
  if (!labReferenceId) {
    return null
  }

  return (
    <ContextMenuItem asChild>
      <a
        href={`https://physician.quanum.questdiagnostics.com/orders-io?LabRefId=${labReferenceId}`}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2"
        onClick={() => {
          void navigator.clipboard.writeText(labReferenceId)
        }}
      >
        <IconExternalLink className="-mt-px size-4 text-foreground-secondary" />
        Open in Quest ({labReferenceId})
      </a>
    </ContextMenuItem>
  )
}

export function DropdownMenuQuestLinkItem({
  to,
}: MenuQuestLinkItemProps): ReactNode {
  const labReferenceId = useLabReferenceId(to)
  if (!labReferenceId) {
    return null
  }

  return (
    <DropdownMenuItem asChild>
      <a
        href={`https://physician.quanum.questdiagnostics.com/orders-io?LabRefId=${labReferenceId}`}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2"
        onClick={() => {
          void navigator.clipboard.writeText(labReferenceId)
        }}
      >
        <IconExternalLink className="-mt-px size-4 text-foreground-secondary" />
        Open in Quest ({labReferenceId})
      </a>
    </DropdownMenuItem>
  )
}

// Change when medplum populates the identifier on ServiceRequest instead of RequestGroup
// https://seenhealth.slack.com/archives/C078NUJP6B1/p1756408462496529
function useLabReferenceId(
  _order: ServiceRequest | Reference<ServiceRequest> | Order
): string | undefined {
  const orderReference = useMemo(() => resolveReference(_order), [_order])

  const { data: order } = useRead(Order, orderReference)
  const orderDate = order?.date
  const requisition = order?.requisition
  const enabled =
    !!order &&
    order.category === OrderCategory.Lab &&
    !!orderDate &&
    !!requisition

  const { data: requestGroups } = useSearch(
    ExtendedRequestGroup,
    {
      patient: order?.participant,
      _filter:
        orderDate &&
        `authored ge ${subHours(orderDate, 3).toISOString()} and authored le ${addHours(
          orderDate,
          3
        ).toISOString()}`,
    },
    { query: { enabled } }
  )
  if (!enabled) {
    return undefined
  }

  const requestGroup =
    requisition &&
    requestGroups?.find((rg) =>
      rg
        .identifiers()
        ?.some(
          (i) =>
            i.system === requisition.system && i.value === requisition.value
        )
    )
  if (!requestGroup) {
    return undefined
  }

  return requestGroup.identifierByType(codeSystems.identifierTypeAccession)
    ?.value
}
